import { useState, useMemo, useEffect } from "react"
import { createPortal } from "react-dom"
import { downloadFile } from "./services/download-file"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { useConversationStatus, useConversationCustomMessage, useConversationLastSeen, useConversationHidden } from "@/features/presence/hooks/useConversationOnline"
import { usePresence, type PresenceStatus } from "@/features/presence/store/presence.store"
import { statusTextClass, statusLabelKey } from "@/features/presence/utils/statusMeta"
import { Phone, Video, UserPlus, Search, Edit2, ImageIcon, Star, Bell, BellOff, Link, Plus, MessageSquareX, LogOut, ChevronRight, MoreVertical, UserX, Shield, ArrowLeft, X, FileText, Mic, Users, Play, Film, Download, CornerUpLeft, Trash2 } from "lucide-react"
import type { Conversation, Message, Participant } from "./types"
import { useMessengerStore } from "./store/messenger.store"
import { useConversationMessages } from "./hooks/useConversationMessages"
import { useDeleteMessage } from "./hooks/useDeleteMessage"
import type { Message as WireMessage } from "./services/messenger.wire"
import { useAuthStore } from "@/features/auth/store/auth.store"
import { useGroupActions } from "./hooks/useGroupActions"
import { messengerApi } from "./services/messenger.api"
import { groupActionReason } from "./group-error"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/clientWeb/components/ui/dropdown-menu"
import { toast } from "@/clientWeb/components/ui/toast-utils"
import { AddMembersDialog } from "./add-members-dialog"
import { EditGroupPage } from "./edit-group-page"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/clientWeb/components/ui/dialog"
import { Button } from "@/clientWeb/components/ui/button"
import { Checkbox } from "@/clientWeb/components/ui/checkbox"
import { ImagePreviewOverlay, type PreviewImageItem } from "./image-preview-overlay"
import { useAttachmentSrc } from "./hooks/useAttachmentSrc"
import { AvatarPhoto } from "./avatar-photo"
import { contactsApi, type SearchResultContact } from "@/features/meetings/services/contacts.api"
import { DocumentPreviewOverlay, type PreviewDocumentItem } from "./document-preview-overlay"
import { resolvePeerEntry } from "./resolve-peer-entry"
import { AttachmentImg, AttachmentLink } from "./private-attachment-ui"

interface MediaVideoItem {
  id: string
  url: string
  thumb?: string
  name: string
  size: number
  /** Sent by the viewer. Decides whether a download control is offered. */
  isSentByMe?: boolean
  /** The message this attachment arrived in, so the gallery can go to it. */
  messageId?: string
}

interface MediaDocItem extends PreviewDocumentItem {
  mime: string
  size: number
}

interface ConversationMedia {
  images: PreviewImageItem[]
  videos: MediaVideoItem[]
  documents: MediaDocItem[]
}

const EMPTY_MEDIA: ConversationMedia = { images: [], videos: [], documents: [] }

/**
 * Everything shared in this thread, split by the tab it belongs to.
 *
 * The three lists used to be module-level mock constants — a dozen photos from
 * a stock-image service, one sample clip repeated three times, and four copies
 * of the same placeholder PDF — so every conversation, DM or group, showed the
 * same dozen strangers' photos and none of what was actually sent in it.
 *
 * Attachments are the source of truth rather than the message `type`: one
 * message can carry several, and the attachment already knows its `kind`,
 * durable `url`, filename and size. Newest first, the order a media gallery is
 * read in — and the order that makes the six-tile strip on the main view show
 * the most recent six.
 */
function collectMedia(messages: WireMessage[], myUserId?: string | null): ConversationMedia {
  const images: PreviewImageItem[] = []
  const videos: MediaVideoItem[] = []
  const documents: MediaDocItem[] = []

  for (let i = messages.length - 1; i >= 0; i--) {
    const mine = !!myUserId && messages[i].senderId === myUserId
    const messageId = messages[i].id
    for (const a of messages[i].attachments ?? []) {
      if (a.kind === "photo") {
        images.push({ id: a.id, url: a.url, name: a.filename ?? "Photo", kind: "image", isSentByMe: mine, messageId })
      } else if (a.kind === "video") {
        videos.push({
          id: a.id,
          url: a.url,
          thumb: a.thumbnailUrl ?? undefined,
          name: a.filename ?? "Video",
          size: a.sizeBytes,
          isSentByMe: mine,
          messageId,
        })
      } else if (a.kind === "document") {
        documents.push({
          id: a.id,
          url: a.url,
          name: a.filename ?? "Document",
          mime: a.mime,
          size: a.sizeBytes,
          isSentByMe: mine,
          messageId,
        })
      }
      // 'audio' / 'voice' deliberately excluded — voice notes are not media
      // anyone browses here, and there is no tab for them.
    }
  }

  return images.length || videos.length || documents.length
    ? { images, videos, documents }
    : EMPTY_MEDIA
}

function formatSize(bytes: number) {
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`
  return `${(bytes / 1024).toFixed(0)} KB`
}

/* ── Sub-components ── */
function StarredMessageItem({ msg }: { msg: Message }) {
  const icon =
    msg.type === "image" ? <ImageIcon className="w-4 h-4 shrink-0 text-muted-foreground" /> :
    msg.type === "audio" ? <Mic className="w-4 h-4 shrink-0 text-muted-foreground" /> :
    msg.type === "file" ? <FileText className="w-4 h-4 shrink-0 text-muted-foreground" /> :
    <Star className="w-4 h-4 shrink-0 text-amber-400" />

  const label =
    msg.type === "image" ? "Photo" :
    msg.type === "audio" ? "Voice message" :
    msg.type === "file" ? (msg.attachmentName || "File") :
    msg.text || ""

  return (
    <div className="flex items-start gap-3 px-4 py-3 hover:bg-muted/40 transition-colors">
      {msg.type === "image" && msg.attachmentUrl ? (
        <AttachmentImg src={msg.attachmentUrl} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" />
      ) : (
        <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">{icon}</div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm text-foreground truncate">{label}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{msg.time} · {msg.isSentByMe ? "You" : msg.senderName || "Contact"}</p>
      </div>
    </div>
  )
}

/* ── Empty tab placeholder ── */
function MediaEmptyState({ icon: Icon, label }: { icon: typeof ImageIcon; label: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-full px-8 py-16 text-center">
      <Icon className="w-10 h-10 text-muted-foreground/30 mb-3" />
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  )
}

/**
 * One media thumbnail, fetched with this client's token.
 *
 * The grid pointed `<img src>` straight at the attachment URL. Those sit
 * behind `requireAuth` and an image element cannot send an Authorization
 * header, so every tile in the info panel came back 401 and rendered broken —
 * the same fault the thread's own bubbles were fixed for, in the one screen
 * whose entire job is showing them.
 */
function MediaThumb({ url, alt, className }: { url?: string; alt: string; className?: string }) {
  const src = useAttachmentSrc(url)
  if (!src) return <div className={`animate-pulse bg-muted ${className ?? ""}`} />
  return <img src={src} alt={alt} className={className} />
}

/** A video's own first frame, for a clip that arrived without a poster. */
function MediaVideoThumb({ url, className }: { url?: string; className?: string }) {
  const src = useAttachmentSrc(url)
  if (!src) return <div className={`animate-pulse bg-muted ${className ?? ""}`} />
  return <video src={src} muted preload="metadata" className={className} />
}

/**
 * "Show in chat" on a gallery tile.
 *
 * A media grid tells you a file exists and nothing about the conversation it
 * belonged to. Reaching that used to mean closing the panel and scrolling the
 * thread by hand looking for it.
 */
function GoToMessageButton({ onClick, className }: { onClick: () => void; className?: string }) {
  const t = useTranslation()
  return (
    <button
      type="button"
      title={t("presence.showInChat")}
      aria-label={t("presence.showInChat")}
      onClick={(e) => { e.stopPropagation(); onClick() }}
      className={`grid h-7 w-7 place-items-center rounded-full bg-black/55 text-white transition-colors hover:bg-black/75 ${className ?? ""}`}
    >
      <CornerUpLeft className="h-3.5 w-3.5" />
    </button>
  )
}

/**
 * A DM peer's email and organisation.
 *
 * Neither is on the conversation: participants carry an id, a name and an
 * avatar and nothing else, so contact info could say who somebody was and not
 * how to reach them or who they work for. The directory has both, for anyone
 * in reach, including a peer outside the viewer's own organisation.
 *
 * Resolved by id first (`/contacts/lookup` on the peer's own
 * contactPresenceId), because the chat's display name is a copy taken when
 * the chat was created and can drift from the directory's current
 * `display_name`; searching by that name could then find nothing for a peer
 * whose directory entry has since changed. The name search this replaced
 * (sub-org, org, global, narrowest match wins via `pickPeerEntry`) only runs
 * as a FALLBACK, for a server old enough to still 404 on `/lookup`, or on the
 * rare id the lookup itself comes back without — and only against the
 * `global` scope, which already contains every narrower one, so there is
 * nothing a second or third call would turn up that the first didn't.
 *
 * Either path is matched on id alone, never on the name used to search:
 * `search` is a partial match on name and email at every scope, so it happily
 * returns other people with a similar name, and resolving by name would
 * eventually put one person's address under another person's face, which is
 * worse than showing nothing. Nothing is what happens when the peer is in
 * neither the lookup nor the fallback list.
 *
 * Two bits of state live at module scope, not in the hook, because they need
 * to survive the panel closing and reopening within the same tab:
 *  - `lookupUnsupported` — once `/contacts/lookup` answers 404 or 405 (no such
 *    route on this server), there's no reason to pay for that call again on
 *    every later Contact Info open; it can only fail the same way again.
 *  - `peerEntryCache` — the resolved entry per peer id, so reopening Contact
 *    Info for someone already looked up this session doesn't lookup or search
 *    again for an answer that won't have changed.
 * A page reload clears both, which is fine: a changed directory shows up on
 * the next session either way.
 */
const peerEntryCache = new Map<string, { email?: string; orgName?: string } | null>()
let lookupUnsupported = false

/** The lookup error message always ends with the HTTP status in parentheses
 *  (`contactsApi.lookup`) — this is how a route that doesn't exist yet (404,
 *  or 405 from a proxy that doesn't recognise it) is told apart from a request
 *  that failed for some other, possibly transient, reason. */
function isRouteMissing(err: unknown): boolean {
  return err instanceof Error && /\((404|405)\)\s*$/.test(err.message)
}

function usePeerDirectoryEntry(peerId: string | undefined, peerName: string | undefined) {
  const [entry, setEntry] = useState<{ email?: string; orgName?: string } | null>(null)

  useEffect(() => {
    if (!peerId) { setEntry(null); return }
    const cached = peerEntryCache.get(peerId)
    if (cached !== undefined) { setEntry(cached); return }
    let cancelled = false
    void (async () => {
      let lookupHit: SearchResultContact | null = null
      if (!lookupUnsupported) {
        try {
          const rows = await contactsApi.lookup([peerId])
          lookupHit = rows.find((r) => r.id === peerId) ?? null
        } catch (err) {
          lookupHit = null
          if (isRouteMissing(err)) lookupUnsupported = true
        }
      }
      if (cancelled) return

      if (lookupHit) {
        const resolved = resolvePeerEntry(lookupHit, [], peerId)
        peerEntryCache.set(peerId, resolved)
        setEntry(resolved)
        return
      }

      // Fallback: one search, global scope, only reached when the lookup had
      // nothing to offer (missing route, no row for this id, or any other
      // failure).
      const name = peerName?.trim() ?? ''
      const result = await contactsApi.search(name, 'global', 200).catch(() => null)
      if (cancelled) return
      const resolved = resolvePeerEntry(null, [result?.contacts ?? null], peerId)
      // Only a real answer is cached: a failed search (network, 500) says
      // nothing about the contact, so the next open asks again.
      if (result) peerEntryCache.set(peerId, resolved)
      setEntry(resolved)
    })()
    return () => { cancelled = true }
  }, [peerId, peerName])

  return entry
}

/* ── Video fullscreen overlay ── */
function VideoPreviewOverlay({ url, name, onClose, isSentByMe, onDeleteClick, onShowInChat }: { url: string; name: string; onClose: () => void; isSentByMe?: boolean; onDeleteClick?: () => void; onShowInChat?: () => void }) {
  const t = useTranslation()
  // The viewer is a history entry of its own.
  //
  // Without this the browser's Back button had nothing local to go back to, so
  // it left Presence altogether and landed on the dashboard — losing the chat,
  // the info panel and the media list the viewer was opened from. Pushing a
  // state makes Back mean "close the viewer", which is what it looks like it
  // should mean, and returns the reader to exactly where they were.
  useEffect(() => {
    window.history.pushState({ videoPreview: true }, '')
    const onPop = () => onClose()
    window.addEventListener('popstate', onPop)
    return () => {
      window.removeEventListener('popstate', onPop)
      // Closed by the X rather than by Back: drop the entry we added, or it
      // would take a second Back press to leave the page.
      if (window.history.state?.videoPreview) window.history.back()
    }
  }, [onClose])

  return (
    // `pointer-events-auto`: this is portaled to the body, and the Radix
    // drawer it is opened from sets `pointer-events: none` there while it is
    // open. Every control in here — the X included — was inert whenever the
    // viewer had been opened from the info panel.
    <div className="fixed inset-0 z-[200] bg-black/95 flex flex-col pointer-events-auto" onClick={onClose}>
      <div className="flex items-center justify-between px-4 py-3 shrink-0" onClick={e => e.stopPropagation()}>
        <span className="text-sm text-white/80 truncate">{name}</span>
        <div className="flex items-center gap-2">
          {/* Recipients only. The sender chose this file off their own disk
              and still has it; offering it back is noise. */}
          {!isSentByMe && (
            <button
              type="button"
              title="Download"
              className="p-2 rounded-full hover:bg-white/10 text-white transition-colors"
              onClick={e => { e.stopPropagation(); void downloadFile(url, name) }}
            >
              <Download className="w-5 h-5" />
            </button>
          )}
          {onShowInChat && (
            <button
              type="button"
              title={t("presence.showInChat")}
              className="p-2 rounded-full hover:bg-white/10 text-white transition-colors"
              onClick={e => { e.stopPropagation(); onShowInChat() }}
            >
              <CornerUpLeft className="w-5 h-5" />
            </button>
          )}
          {onDeleteClick && (
            <button
              type="button"
              title={t("common.delete")}
              className="p-2 rounded-full hover:bg-red-500/30 text-white transition-colors"
              onClick={e => { e.stopPropagation(); onDeleteClick() }}
            >
              <Trash2 className="w-5 h-5" />
            </button>
          )}
          <button className="p-2 rounded-full hover:bg-white/10 text-white transition-colors" onClick={onClose}>
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-4" onClick={e => e.stopPropagation()}>
        <video
          src={url}
          controls
          autoPlay
          className="max-w-full max-h-full rounded-xl"
          onClick={e => e.stopPropagation()}
        />
      </div>
    </div>
  )
}

/* ── Props ── */
interface ProfileInfoPanelProps {
  /** Go to the message an attachment arrived in, and close this panel. The
   *  gallery is a list of things that were SAID at some point in the thread;
   *  finding what was around one used to mean scrolling for it by hand. */
  onJumpToMessage?: (messageId: string) => void
  conversation: Conversation
  starredMessages?: Message[]
  onClose: () => void
  onStartAudioCall?: () => void
  onStartVideoCall?: () => void
  /**
   * How many messages "clear chat" would remove — system notices excluded.
   *
   * Same rule as the conversation header's menu: an empty thread has nothing to
   * clear, so the action is not offered. Defaults to 1 so a caller that has not
   * been updated keeps the option rather than losing it silently.
   */
  clearableCount?: number
  /** Both carry the dialog's "Keep starred messages" choice — the server applies
   *  it at clear time, so it can't be decided later. */
  onClearChat?: (opts: { keepStarred: boolean }) => void
  onDeleteChat?: (opts: { keepStarred: boolean }) => void
  /** Really leave the group, dropping my participant row. Group-only. */
  onExitGroup?: () => void
  onMakeAdmin?: (memberId: string) => void
  onRemoveAdmin?: (memberId: string) => void
  onRemoveMember?: (memberId: string) => void
  onUpdateProfile?: (data: { name?: string; imageUrl?: string; description?: string }) => void
  /** Open (or start) a direct chat with one of the group's members. */
  onMessageMember?: (member: { id: string; name: string; imageUrl?: string }) => void
}

type PanelView = "main" | "starred" | "media" | "edit"
type MediaTab = "images" | "videos" | "documents"

function MemberOnlineLabel({ memberId, fallback }: { memberId: string; fallback: boolean }) {
  const t = useTranslation()
  const live = usePresence(memberId)
  const status: PresenceStatus = memberId ? live.status : (fallback ? 'online' : 'offline')
  if (status === 'offline') return null
  return <p className={`text-[10px] ${statusTextClass[status]}`}>{t(statusLabelKey[status])}</p>
}

export function ProfileInfoPanel({
  conversation, starredMessages = [], onClose,
  onStartAudioCall, onStartVideoCall,
  clearableCount = 1, onClearChat, onDeleteChat, onExitGroup,
  onMakeAdmin, onRemoveAdmin, onRemoveMember,
  onUpdateProfile, onJumpToMessage, onMessageMember,
}: ProfileInfoPanelProps) {
  const t = useTranslation()
  const c = conversation
  const conversations = useMessengerStore(s => s.conversations)
  // Live wire conversation — the source of truth for group members / title /
  // avatar. Group-management mutations reconcile into it, so reading from here
  // makes the member list + header update immediately after add/remove/rename.
  const wireConv = useMessengerStore(s => s.wireConversations.get(c.id))
  // I left this group (or was removed): it stays readable, and Delete group
  // replaces Exit group.
  const hasLeftGroup = useMessengerStore(s => s.removedFromConversationIds.has(c.id))
  const myUserId = useAuthStore(s => s.presenceId) ?? null
  const groupActions = useGroupActions()

  const [muteNotifications, setMuteNotifications] = useState(false)
  const [addMembersOpen, setAddMembersOpen] = useState(false)
  const [confirmType, setConfirmType] = useState<null | "clear" | "delete" | "exit" | "deleteGroup">(null)
  // Default on, re-armed on each open — see the same choice in conversation-header.
  const [keepStarred, setKeepStarred] = useState(true)
  const [memberSearch, setMemberSearch] = useState("")
  const [memberSearchOpen, setMemberSearchOpen] = useState(false)
  const [view, setView] = useState<PanelView>("main")
  const [mediaTab, setMediaTab] = useState<MediaTab>("images")
  const [imagePreviewIndex, setImagePreviewIndex] = useState<number | null>(null)
  const [avatarPreviewOpen, setAvatarPreviewOpen] = useState(false)
  const [videoPreview, setVideoPreview] = useState<{ id: string; url: string; name: string; isSentByMe?: boolean; messageId?: string } | null>(null)
  const [pdfPreview, setPdfPreview] = useState<PreviewDocumentItem | null>(null)
  // The pending scope decision for a delete raised from any of the three media
  // kinds: one dialog, so "delete for everyone" only has to be built once.
  const [mediaDeleteTarget, setMediaDeleteTarget] = useState<{ kind: "image" | "video" | "document"; attachmentId: string; messageId: string } | null>(null)
  const [mediaDeleteForEveryone, setMediaDeleteForEveryone] = useState(false)

  // Same hook the conversation panel uses, so this reads the one thread the
  // rest of the app renders. It seeds a conversation's history at most once,
  // which matters on the chat-list route where the thread was never opened.
  const { messages: wireMessages, isLoading: isLoadingMedia } = useConversationMessages(c.id)
  const media = useMemo(() => collectMedia(wireMessages, myUserId), [wireMessages, myUserId])
  // The strip's six tiles: images and videos both belong there (documents do
  // not get a thumbnail), tagged so the render below knows which viewer to
  // open. Images and videos are each already newest-first within their own
  // list, so concatenating keeps that order per kind.
  const stripItems = useMemo(
    () => [
      ...media.images.map((img) => ({ kind: "image" as const, id: img.id, data: img })),
      ...media.videos.map((vid) => ({ kind: "video" as const, id: vid.id, data: vid })),
    ].slice(0, 6),
    [media],
  )
  const { remove: deleteMessageReal, canDeleteForEveryone: canDeleteForEveryoneWire } = useDeleteMessage()

  /**
   * Raise the scope-choice dialog for one media item.
   *
   * Every caller (an overlay's own delete button, or a document row's trash
   * icon) hands back the ATTACHMENT id, not a message id: deleting needs the
   * message that carries it, so this looks the item up in whichever list
   * already has both before anything is shown.
   */
  const openMediaDeleteConfirm = (kind: "image" | "video" | "document", attachmentId: string) => {
    const list = kind === "image" ? media.images : kind === "video" ? media.videos : media.documents
    const item = list.find((d) => d.id === attachmentId)
    if (!item?.messageId) return
    setMediaDeleteForEveryone(false)
    setMediaDeleteTarget({ kind, attachmentId, messageId: item.messageId })
  }

  // Only the sender of a not-yet-deleted message, sent within the last 15
  // minutes, may retract it, same rule the thread itself uses. Everyone else
  // only ever gets "for me".
  const mediaDeleteMessage = mediaDeleteTarget
    ? wireMessages.find((m) => m.id === mediaDeleteTarget.messageId)
    : undefined
  const mediaDeleteCanEveryone = !!mediaDeleteMessage && canDeleteForEveryoneWire(mediaDeleteMessage)

  const confirmMediaDelete = async () => {
    if (!mediaDeleteTarget) return
    const scope = mediaDeleteForEveryone && mediaDeleteCanEveryone ? "everyone" : "me"
    const ok = await deleteMessageReal(c.id, mediaDeleteTarget.messageId, scope)
    setMediaDeleteTarget(null)
    if (!ok) toast.error({ title: t("presence.documentDeleteFailed") })
  }

  /** Resolve an attachment id back to the message it arrived in, and jump. */
  const jumpToMediaMessage = (kind: "image" | "video" | "document", attachmentId: string) => {
    if (!onJumpToMessage) return
    const list = kind === "image" ? media.images : kind === "video" ? media.videos : media.documents
    const messageId = list.find((d) => d.id === attachmentId)?.messageId
    if (messageId) onJumpToMessage(messageId)
  }

  /**
   * Is this a group?
   *
   * From the server's own `kind` when the wire conversation has loaded, because
   * that is where the fact lives; the local flag is the fallback for a
   * conversation the store has not caught up with.
   *
   * It used to be `c.isGroup || !!c.participants`, and the second half is true
   * of every DM — a one-to-one conversation has a participants array with two
   * people in it, which is how the call button finds the peer to ring. So every
   * DM's info screen claimed to be a group: group title and avatar, a member
   * count, and "Exit group" where "Delete chat" belongs.
   */
  const isGroup = wireConv ? wireConv.kind === 'group' : c.isGroup
  const peerStatus = useConversationStatus(c)
  const peerCustomMessage = useConversationCustomMessage(c)
  const peerLastSeen = useConversationLastSeen(c)
  // Same rule as the chat header: a contact who hides their status shows no
  // status line and no custom message here either.
  const peerHidden = useConversationHidden(c)
  // Prefer the live wire title/avatar for groups so renames reflect at once.
  const liveTitle = isGroup ? (wireConv?.title ?? c.name) : c.name
  const liveImage = isGroup ? (wireConv?.avatarUrl ?? c.imageUrl) : c.imageUrl
  const initials = liveTitle.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()
  const colors = ["bg-primary/20 text-primary", "bg-violet-500/20 text-violet-600", "bg-emerald-500/20 text-emerald-600", "bg-amber-500/20 text-amber-600"]
  const colorIdx = liveTitle.charCodeAt(0) % colors.length
  // Members from the wire store (live), falling back to the UI prop before hydrate.
  const members: Participant[] = wireConv?.participants
    ? wireConv.participants.map(p => ({
        id: p.userId,
        name: p.name ?? `User ${p.userId.slice(0, 8)}`,
        imageUrl: p.avatarUrl ?? undefined,
        isAdmin: p.isAdmin,
      }))
    : (c.participants || [])
  // The other person in a DM. A group has no single peer, so no lookup.
  const peerId = !isGroup ? members.find((m) => m.id !== myUserId)?.id : undefined
  const peerName = !isGroup ? members.find((m) => m.id !== myUserId)?.name : undefined
  const peerDirectory = usePeerDirectoryEntry(peerId, peerName)

  const myIsAdmin = wireConv?.participants?.find(p => p.userId === myUserId)?.isAdmin ?? false
  const filteredMembers = memberSearch.trim()
    ? members.filter(m => m.name.toLowerCase().includes(memberSearch.toLowerCase()))
    : members
  const groupsInCommon = isGroup ? [] : conversations.filter(conv => conv.isGroup && conv.participants?.some(p => p.id === c.id))

  /**
   * The "Created on" line at the foot of a group's panel.
   *
   * `createdAt` lives on the wire conversation, not the UI prop, so this
   * waits on the same hydrate the member list does rather than ever showing
   * a placeholder date. Groups only: a direct chat's creation date says
   * nothing useful about the person. A group names its creator
   * only when a name for that id is already on hand, and never says "you"
   * unless the id actually matches this viewer.
   */
  const createdAtDate = wireConv?.createdAt ? new Date(wireConv.createdAt) : null
  const createdAtLabel = createdAtDate && !Number.isNaN(createdAtDate.getTime())
    ? createdAtDate.toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" })
    : null
  const groupCreatedByMe = isGroup && !!myUserId && wireConv?.createdBy === myUserId
  const groupCreatorName = isGroup && wireConv?.createdBy && !groupCreatedByMe
    ? members.find((m) => m.id === wireConv.createdBy)?.name
    : undefined

  /**
   * The group's invite link. Built here, not fetched.
   *
   * It is the conversation id in a URL, so there is nothing to request and
   * nothing to wait for — the button is ready the moment the panel is. The
   * trade is that the link never expires and cannot be revoked: whoever ends
   * up holding it can join. The server's org check is what stops an id that
   * leaked from opening this group to another organisation.
   */
  // `wireConv.id` is the GROUP's conversation id, taken from the server's own
  // record rather than the UI object, so there is no doubt about which id the
  // link carries. This block only renders for groups, so it is always a group.
  const inviteGroupId = wireConv?.id ?? c.id
  const inviteUrl = `${window.location.origin}/chats/invite/${inviteGroupId}`

  /**
   * Copy it. No request here at all.
   *
   * The clipboard needs a secure context, and this runs over plain http in
   * some deployments — so on failure the link goes in the toast rather than
   * the click appearing to do nothing.
   */
  const handleInviteViaLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl)
      toast.success({ title: t("presence.inviteLinkCopied") })
    } catch {
      toast.info({ title: t("presence.inviteViaLink"), description: inviteUrl })
    }
  }

  // ── Group-management handlers (real API + store reconcile) ──
  const handleAddMembers = async (userIds: string[]) => {
    try {
      await groupActions.addMembers(c.id, userIds)
      toast.success({ title: t("presence.membersAdded"), description: `${userIds.length} member${userIds.length > 1 ? "s" : ""} added to ${liveTitle}.` })
    } catch (err) {
      toast.error({ title: t("presence.membersAddFailed"), description: groupActionReason(err, t) })
    }
  }

  const handleSetAdmin = async (memberId: string, makeAdmin: boolean) => {
    if (makeAdmin) onMakeAdmin?.(memberId)
    else onRemoveAdmin?.(memberId)
    try {
      await groupActions.setAdmin(c.id, memberId, makeAdmin)
    } catch (err) {
      toast.error({ title: t("presence.adminUpdateFailed"), description: groupActionReason(err, t) })
    }
  }

  const handleRemoveMember = async (memberId: string) => {
    onRemoveMember?.(memberId)
    try {
      await groupActions.removeMember(c.id, memberId)
      toast.success({ title: t("presence.memberRemoved") })
    } catch (err) {
      toast.error({ title: t("presence.memberRemoveFailed"), description: groupActionReason(err, t) })
    }
  }

  const handleSaveGroupEdit = async (data: { name: string; imageFile?: File; imageRemoved: boolean }) => {
    try {
      let avatarUrl: string | null | undefined
      if (data.imageRemoved) {
        avatarUrl = null
      } else if (data.imageFile) {
        avatarUrl = await messengerApi.uploadAndResolveUrl(data.imageFile, "image")
      }
      await groupActions.renameGroup(c.id, data.name, avatarUrl)
      onUpdateProfile?.({ name: data.name, imageUrl: avatarUrl ?? undefined })
      setView("main")
      toast.success({ title: t("presence.groupUpdated") })
    } catch (err) {
      const msg = groupActionReason(err, t)
      toast.error({ title: t("presence.groupUpdateFailed"), description: msg })
    }
  }

  /* ── Starred view ── */
  if (view === "starred") {
    return (
      <div className="flex flex-col h-full w-full">
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-border shrink-0">
          <button onClick={() => setView("main")} className="p-1.5 rounded-full hover:bg-muted text-muted-foreground transition-colors shrink-0">
            <ArrowLeft className="w-4.5 h-4.5" />
          </button>
          <h3 className="flex-1 text-sm font-semibold text-foreground">{t("presence.starredMessages")}</h3>
        </div>
        <div className="flex-1 overflow-y-auto">
          {starredMessages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-3">
              <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center">
                <Star className="w-6 h-6 text-muted-foreground/50" />
              </div>
              <p className="text-sm text-muted-foreground">No starred messages</p>
            </div>
          ) : (
            <div className="divide-y divide-border/50">
              {starredMessages.map(msg => <StarredMessageItem key={msg.id} msg={msg} />)}
            </div>
          )}
        </div>
      </div>
    )
  }

  /* ── Media view ── */
  if (view === "media") {
    return (
      <div className="flex flex-col h-full w-full">
        {/* Media header */}
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-border shrink-0">
          <button onClick={() => setView("main")} className="p-1.5 rounded-full hover:bg-muted text-muted-foreground transition-colors shrink-0">
            <ArrowLeft className="w-4.5 h-4.5" />
          </button>
          <h3 className="flex-1 text-sm font-semibold text-foreground">{t("presence.media")}</h3>
        </div>

        {/* Tab bar */}
        <div className="flex border-b border-border shrink-0">
          {(["images", "videos", "documents"] as MediaTab[]).map(tab => (
            <button
              key={tab}
              onClick={() => setMediaTab(tab)}
              className={`flex-1 py-2.5 text-xs font-medium capitalize transition-colors border-b-2 -mb-px ${
                mediaTab === tab
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {tab === "images" ? `Images (${media.images.length})` : tab === "videos" ? `Videos (${media.videos.length})` : `Docs (${media.documents.length})`}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto">
          {mediaTab === "images" && (
            media.images.length === 0 ? (
              <MediaEmptyState icon={ImageIcon} label={isLoadingMedia ? "Loading…" : "No images shared yet"} />
            ) : (
              <div className="grid grid-cols-3 gap-0.5 p-0.5">
                {media.images.map((img, i) => (
                  <div key={img.id} className="group relative aspect-square overflow-hidden">
                    <button
                      onClick={() => setImagePreviewIndex(i)}
                      className="block h-full w-full hover:opacity-90 transition-opacity"
                    >
                      <MediaThumb url={img.url} alt={img.name} className="w-full h-full object-cover" />
                    </button>
                    {onJumpToMessage && img.messageId && (
                      <GoToMessageButton
                        onClick={() => onJumpToMessage(img.messageId!)}
                        className="absolute right-1 top-1 opacity-0 transition-opacity group-hover:opacity-100"
                      />
                    )}
                  </div>
                ))}
              </div>
            )
          )}

          {mediaTab === "videos" && (
            media.videos.length === 0 ? (
              <MediaEmptyState icon={Film} label={isLoadingMedia ? "Loading…" : "No videos shared yet"} />
            ) : (
              <div className="grid grid-cols-2 gap-1 p-1">
                {media.videos.map(vid => (
                  <div key={vid.id} className="group relative aspect-video overflow-hidden rounded-xl bg-black">
                  {onJumpToMessage && vid.messageId && (
                    <GoToMessageButton
                      onClick={() => onJumpToMessage(vid.messageId!)}
                      className="absolute right-1 top-1 z-10 opacity-0 transition-opacity group-hover:opacity-100"
                    />
                  )}
                  <button
                    onClick={() => setVideoPreview({ id: vid.id, url: vid.url, name: vid.name, isSentByMe: vid.isSentByMe, messageId: vid.messageId })}
                    className="block h-full w-full"
                  >
                    {/* Posters are optional on a real upload; without one the
                        video element's own first frame stands in for it. */}
                    {vid.thumb ? (
                      <MediaThumb url={vid.thumb} alt={vid.name} className="w-full h-full object-cover" />
                    ) : (
                      <MediaVideoThumb url={vid.url} className="w-full h-full object-cover" />
                    )}
                    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                      <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                        <Play className="w-5 h-5 text-white fill-white ml-0.5" />
                      </div>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/60 to-transparent">
                      <p className="text-[10px] text-white truncate">{vid.name}</p>
                      <p className="text-[9px] text-white/70">{formatSize(vid.size)}</p>
                    </div>
                  </button>
                  </div>
                ))}
              </div>
            )
          )}

          {mediaTab === "documents" && (
            media.documents.length === 0 ? (
              <MediaEmptyState icon={FileText} label={isLoadingMedia ? "Loading…" : "No documents shared yet"} />
            ) : (
              <div className="divide-y divide-border/50">
                {media.documents.map(doc => {
                  // Formats the viewer can render. Everything else is a link
                  // out, as before — and the PDF-only test this replaced sent
                  // Word documents, text and CSV down that path even though
                  // the viewer handles all three now.
                  const viewerExt = doc.name.split(".").pop()?.toLowerCase() ?? ""
                  const canPreview =
                    doc.mime === "application/pdf" ||
                    ["pdf", "docx", "doc", "txt", "log", "md", "csv"].includes(viewerExt)
                  const label = doc.name.split(".").pop()?.toUpperCase()
                  const meta = `${label && label !== doc.name ? `${label} · ` : ""}${formatSize(doc.size)}`
                  const body = (
                    <>
                      <div className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-950/30 flex items-center justify-center shrink-0">
                        <FileText className="w-5 h-5 text-red-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-foreground truncate">{doc.name}</p>
                        <p className="text-xs text-muted-foreground">{meta}</p>
                      </div>
                    </>
                  )
                  const rowClass = "w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/40 transition-colors text-left"
                  // Only PDFs can be shown in the PDF viewer. Anything else —
                  // a .docx, a .xlsx, a .zip — opens the way the browser
                  // handles it, instead of a viewer that renders nothing.
                  const jump = onJumpToMessage && doc.messageId
                    ? (
                        <GoToMessageButton
                          onClick={() => onJumpToMessage(doc.messageId!)}
                          className="shrink-0 opacity-60 hover:opacity-100"
                        />
                      )
                    : null
                  // Trash icon for the row itself: a document opened via a
                  // plain link has no viewer header to hang a delete action
                  // off, so it needs its own control right here, same as the
                  // jump button beside it.
                  const deleteBtn = (
                    <button
                      type="button"
                      onClick={() => openMediaDeleteConfirm("document", doc.id)}
                      title={t("common.delete")}
                      aria-label={t("common.delete")}
                      className="shrink-0 p-2 mr-1 rounded-full text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )
                  return canPreview ? (
                    <div key={doc.id} className="flex items-center">
                      <button onClick={() => setPdfPreview(doc)} className={`${rowClass} flex-1 min-w-0`}>
                        {body}
                      </button>
                      {jump && <div className="pr-3">{jump}</div>}
                    </div>
                  ) : (
                    <div key={doc.id} className="flex items-center">
                      <AttachmentLink href={doc.url} className={`${rowClass} flex-1 min-w-0`}>
                        {body}
                      </AttachmentLink>
                      {jump && <div className="pr-1">{jump}</div>}
                      {deleteBtn}
                    </div>
                  )
                })}
              </div>
            )
          )}
        </div>

        {/* Overlays — portaled to body to escape Drawer's transform stacking context */}
        {imagePreviewIndex !== null && createPortal(
          <ImagePreviewOverlay
            open
            images={media.images}
            currentIndex={imagePreviewIndex}
            onClose={() => setImagePreviewIndex(null)}
            onChangeIndex={setImagePreviewIndex}
            onDeleteCurrent={(id) => { setImagePreviewIndex(null); openMediaDeleteConfirm("image", id) }}
            confirmDelete={false}
            showInChatLabel={t("presence.showInChat")}
            onShowInChat={onJumpToMessage ? (id) => { setImagePreviewIndex(null); jumpToMediaMessage("image", id) } : undefined}
          />,
          document.body
        )}
        {videoPreview && createPortal(
          <VideoPreviewOverlay
            url={videoPreview.url}
            name={videoPreview.name}
            isSentByMe={videoPreview.isSentByMe}
            onClose={() => setVideoPreview(null)}
            onDeleteClick={() => { const id = videoPreview.id; setVideoPreview(null); openMediaDeleteConfirm("video", id) }}
            onShowInChat={onJumpToMessage ? () => { const id = videoPreview.id; setVideoPreview(null); jumpToMediaMessage("video", id) } : undefined}
          />,
          document.body
        )}
        {createPortal(
          <DocumentPreviewOverlay
            open={!!pdfPreview}
            pdf={pdfPreview}
            onClose={() => setPdfPreview(null)}
            onDeleteCurrent={(id) => { setPdfPreview(null); openMediaDeleteConfirm("document", id) }}
            confirmDelete={false}
            showInChatLabel={t("presence.showInChat")}
            onShowInChat={onJumpToMessage ? (id) => { setPdfPreview(null); jumpToMediaMessage("document", id) } : undefined}
          />,
          document.body
        )}

        {/* Delete-scope dialog, shared by images, videos and documents. The
            "everyone" choice only ever appears for your own, not-yet-deleted
            message; anyone else's item can only be dropped for you. */}
        <Dialog
          open={!!mediaDeleteTarget}
          onOpenChange={(open) => { if (!open) setMediaDeleteTarget(null) }}
        >
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>{t("presence.deleteMediaTitle")}</DialogTitle>
            </DialogHeader>
            {mediaDeleteCanEveryone && (
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <Checkbox checked={mediaDeleteForEveryone} onCheckedChange={(v) => setMediaDeleteForEveryone(v === true)} />
                {t("presence.deleteForEveryone")}
              </label>
            )}
            <DialogFooter className="pt-2">
              <Button variant="outline" onClick={() => setMediaDeleteTarget(null)}>{t("common.cancel")}</Button>
              <Button variant="destructive" onClick={() => void confirmMediaDelete()}>{t("common.delete")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    )
  }

  /* ── Edit group view (admin only) ── */
  if (view === "edit") {
    return (
      <EditGroupPage
        groupId={c.id}
        currentName={liveTitle}
        currentImage={liveImage}
        onClose={() => setView("main")}
        onSave={handleSaveGroupEdit}
      />
    )
  }

  /* ── Main view ── */
  return (
    <div className="flex flex-col h-full w-full">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3.5 border-b border-border shrink-0">
        <button onClick={onClose} className="p-1.5 rounded-full hover:bg-muted text-muted-foreground transition-colors shrink-0">
          <ArrowLeft className="w-4.5 h-4.5" />
        </button>
        <h3 className="flex-1 text-sm font-semibold text-foreground truncate">
          {isGroup ? t("presence.groupInfo") : t("presence.contactInfo")}
        </h3>
        {/* Rename is a GROUP action, and an admin-only one — it goes through
            `renameGroup` on the API. A DM's title is the other person's own
            profile name: editing it here only rewrote local state, so the name
            snapped back on reload. No pencil on a contact. */}
        {isGroup && myIsAdmin && (
          <button onClick={() => setView("edit")} className="p-1.5 rounded-full hover:bg-muted text-primary transition-colors shrink-0">
            <Edit2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {/* Avatar + name */}
        <div className="flex flex-col items-center py-6 px-4">
          <div className="relative">
            {/* A picture opens full size here; changing it lives on the Edit
                screen (the pencil), so this click only ever views. Initials
                have nothing to enlarge and stay inert. */}
            {liveImage ? (
              <button
                type="button"
                onClick={() => setAvatarPreviewOpen(true)}
                aria-label={liveTitle}
                className={`w-20 h-20 rounded-full flex items-center justify-center shrink-0 overflow-hidden cursor-pointer hover:opacity-90 transition-opacity ${colors[colorIdx]}`}
              >
                <AvatarPhoto
                  src={liveImage}
                  alt={liveTitle}
                  className="w-full h-full object-cover"
                  fallback={<span className="text-2xl font-semibold">{initials}</span>}
                  contactId={!isGroup ? peerId : undefined}
                />
              </button>
            ) : (
              <div className={`w-20 h-20 rounded-full flex items-center justify-center text-2xl font-semibold shrink-0 overflow-hidden ${colors[colorIdx]}`}>
                {initials}
              </div>
            )}
          </div>
          {avatarPreviewOpen && liveImage && createPortal(
            <ImagePreviewOverlay
              open
              images={[{ id: `avatar-${c.id}`, url: liveImage, name: liveTitle }]}
              currentIndex={0}
              onClose={() => setAvatarPreviewOpen(false)}
              onChangeIndex={() => {}}
            />,
            document.body
          )}

          {/* `max-w-xs` alone caps the box, not the text inside it: a name
              with no spaces (the 100-char limit allows one) has nowhere to
              break and painted past the edge, widening the scroll area of
              the panel around it rather than wrapping. `min-w-0` lets this
              flex item shrink below that unbroken run and `overflow-wrap`
              gives the browser somewhere to break it. */}
          <h2 className="text-lg font-medium text-foreground text-center max-w-xs min-w-0 mt-3 [overflow-wrap:anywhere]">{liveTitle}</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {isGroup
              ? `${members.length} ${t("presence.members")}`
              : peerHidden
                ? null
                : peerStatus !== 'offline'
                  ? t(statusLabelKey[peerStatus])
                  : peerLastSeen
                    ? t("presence.lastSeenAt", { time: peerLastSeen })
                    : t(statusLabelKey.offline)}
          </p>
          {/* Their custom status. Contact info is where you look for it, and
              until now the only person who could see a status was whoever set
              it. Groups have no single author, so it stays a DM affordance. */}
          {!isGroup && !peerHidden && peerCustomMessage && (
            <p className="text-sm text-foreground text-center max-w-xs mt-1.5 italic">
              “{peerCustomMessage}”
            </p>
          )}
          {/* Email and organisation — the two things that say who this is
              beyond a display name. Each renders only when the directory
              actually has it; an empty row labelled "Email" tells the reader
              less than no row at all. */}
          {!isGroup && (peerDirectory?.email || peerDirectory?.orgName) && (
            <div className="mt-3 flex flex-col items-center gap-1">
              {peerDirectory.email && (
                <a
                  href={`mailto:${peerDirectory.email}`}
                  className="text-sm text-primary hover:underline break-all"
                >
                  {peerDirectory.email}
                </a>
              )}
              {peerDirectory.orgName && (
                <p className="text-xs text-muted-foreground">{peerDirectory.orgName}</p>
              )}
            </div>
          )}
        </div>

        {/* Action tiles */}
        <div className="px-4 mb-3">
          {isGroup ? (
            <div className="grid grid-cols-3 gap-2">
              <button onClick={onStartAudioCall} className="flex flex-col items-center gap-1.5 p-3 rounded-2xl bg-muted/50 hover:bg-muted transition-colors">
                <Phone className="w-5 h-5 text-primary" />
                <span className="text-xs text-foreground">{t("presence.callAction")}</span>
              </button>
              <button onClick={onStartVideoCall} className="flex flex-col items-center gap-1.5 p-3 rounded-2xl bg-muted/50 hover:bg-muted transition-colors">
                <Video className="w-5 h-5 text-primary" />
                <span className="text-xs text-foreground">{t("presence.videoAction")}</span>
              </button>
              <button onClick={() => setAddMembersOpen(true)} className="flex flex-col items-center gap-1.5 p-3 rounded-2xl bg-muted/50 hover:bg-muted transition-colors">
                <UserPlus className="w-5 h-5 text-primary" />
                <span className="text-xs text-foreground">{t("presence.addMembersAction")}</span>
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <button onClick={onStartAudioCall} className="flex flex-col items-center gap-1.5 p-3 rounded-2xl bg-muted/50 hover:bg-muted transition-colors">
                <Phone className="w-5 h-5 text-primary" />
                <span className="text-xs text-foreground">{t("presence.callAction")}</span>
              </button>
              <button onClick={onStartVideoCall} className="flex flex-col items-center gap-1.5 p-3 rounded-2xl bg-muted/50 hover:bg-muted transition-colors">
                <Video className="w-5 h-5 text-primary" />
                <span className="text-xs text-foreground">{t("presence.videoAction")}</span>
              </button>
            </div>
          )}
        </div>

        {/* Media strip */}
        <div className="mx-4 mb-3 rounded-2xl border border-border bg-card overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
            <div className="flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">{t("presence.media")}</span>
            </div>
            <button onClick={() => setView("media")} className="flex items-center gap-1 text-xs text-primary hover:underline">
              {t("presence.viewAll")} <ChevronRight className="w-3 h-3" />
            </button>
          </div>
          {media.images.length + media.videos.length + media.documents.length === 0 ? (
            <p className="px-4 py-6 text-center text-xs text-muted-foreground">
              {isLoadingMedia ? t("presence.loading") : t("presence.noMediaShared")}
            </p>
          ) : stripItems.length > 0 ? (
            <div className="grid grid-cols-3 gap-0.5 p-0.5">
              {stripItems.map((entry) =>
                entry.kind === "image" ? (
                  <div key={entry.id} className="group relative aspect-square overflow-hidden">
                    <button
                      onClick={() => { setView("media"); setMediaTab("images"); setImagePreviewIndex(media.images.findIndex((im) => im.id === entry.id)) }}
                      className="block h-full w-full hover:opacity-90 transition-opacity"
                    >
                      <MediaThumb url={entry.data.url} alt={entry.data.name} className="w-full h-full object-cover" />
                    </button>
                    {onJumpToMessage && entry.data.messageId && (
                      <GoToMessageButton
                        onClick={() => onJumpToMessage(entry.data.messageId!)}
                        className="absolute right-1 top-1 opacity-0 transition-opacity group-hover:opacity-100"
                      />
                    )}
                  </div>
                ) : (
                  <div key={entry.id} className="group relative aspect-square overflow-hidden bg-black">
                    <button
                      onClick={() => { setView("media"); setMediaTab("videos") }}
                      className="block h-full w-full hover:opacity-90 transition-opacity"
                    >
                      {entry.data.thumb ? (
                        <MediaThumb url={entry.data.thumb} alt={entry.data.name} className="w-full h-full object-cover" />
                      ) : (
                        <MediaVideoThumb url={entry.data.url} className="w-full h-full object-cover" />
                      )}
                      <div className="absolute inset-0 bg-black/25 flex items-center justify-center">
                        <Film className="w-5 h-5 text-white" />
                      </div>
                    </button>
                    {onJumpToMessage && entry.data.messageId && (
                      <GoToMessageButton
                        onClick={() => onJumpToMessage(entry.data.messageId!)}
                        className="absolute right-1 top-1 z-10 opacity-0 transition-opacity group-hover:opacity-100"
                      />
                    )}
                  </div>
                )
              )}
            </div>
          ) : (
            // No image or video to thumbnail: a documents-only chat used to
            // render this empty grid with nothing in it, which looked like the
            // card itself was broken rather than like a chat with only files.
            <div className="divide-y divide-border/50">
              {media.documents.slice(0, 3).map((doc) => (
                <button
                  key={doc.id}
                  onClick={() => { setView("media"); setMediaTab("documents") }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-muted/40 transition-colors text-left"
                >
                  <div className="w-8 h-8 rounded-lg bg-red-50 dark:bg-red-950/30 flex items-center justify-center shrink-0">
                    <FileText className="w-4 h-4 text-red-500" />
                  </div>
                  <span className="flex-1 min-w-0 truncate text-xs text-foreground">{doc.name}</span>
                </button>
              ))}
              {media.documents.length > 3 && (
                <button
                  onClick={() => { setView("media"); setMediaTab("documents") }}
                  className="w-full px-4 py-2 text-left text-xs text-primary hover:underline"
                >
                  +{media.documents.length - 3} {t("common.more")}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Starred messages */}
        <div className="mx-4 mb-3 rounded-2xl border border-border bg-card overflow-hidden">
          <button onClick={() => setView("starred")} className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors">
            <div className="flex items-center gap-3">
              <Star className="w-5 h-5 text-muted-foreground" />
              <span className="text-sm text-foreground">{t("presence.starredMessages")}</span>
            </div>
            <div className="flex items-center gap-1.5">
              {starredMessages.length > 0 && <span className="text-xs text-muted-foreground">{starredMessages.length}</span>}
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </div>
          </button>
        </div>

        {/* Mute notifications */}
        <div className="mx-4 mb-3 rounded-2xl border border-border bg-card overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-3">
              {muteNotifications ? <BellOff className="w-5 h-5 text-muted-foreground" /> : <Bell className="w-5 h-5 text-muted-foreground" />}
              <span className="text-sm text-foreground">{t("presence.notificationsMuted")}</span>
            </div>
            <button
              onClick={() => {
                const next = !muteNotifications
                setMuteNotifications(next)
                toast.info({ title: next ? t("presence.notificationsMuted") : t("presence.notificationsUnmuted"), description: next ? t("presence.notificationsMutedDesc") : t("presence.notificationsUnmutedDesc") })
              }}
              className={`w-11 h-6 rounded-full transition-colors ${muteNotifications ? "bg-primary" : "bg-muted"}`}
            >
              <div className={`w-5 h-5 rounded-full bg-background transition-transform ${muteNotifications ? "translate-x-5" : "translate-x-0.5"}`} />
            </button>
          </div>
        </div>

        {/* Members section — groups only */}
        {isGroup && (
          <div className="mx-4 mb-3 rounded-2xl border border-border bg-card overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-border min-h-[48px]">
              {memberSearchOpen ? (
                <>
                  <Search className="w-4 h-4 text-muted-foreground shrink-0" />
                  <input
                    autoFocus
                    value={memberSearch}
                    onChange={e => setMemberSearch(e.target.value)}
                    placeholder={t("presence.searchMembers")}
                    className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
                  />
                  <button onClick={() => { setMemberSearchOpen(false); setMemberSearch("") }} className="shrink-0">
                    <X className="w-4 h-4 text-muted-foreground hover:text-foreground transition-colors" />
                  </button>
                </>
              ) : (
                <>
                  <p className="flex-1 text-sm font-medium text-foreground">{t("presence.members")} ({members.length})</p>
                  <button onClick={() => setMemberSearchOpen(true)} className="p-1.5 rounded-full hover:bg-muted text-muted-foreground transition-colors shrink-0">
                    <Search className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>

            <button onClick={() => setAddMembersOpen(true)} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-muted/50 transition-colors">
              <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center">
                <Plus className="w-5 h-5 text-primary" />
              </div>
              <span className="text-sm text-foreground">{t("presence.addMembersBtn")}</span>
            </button>
            {/* Shown to every member, as it was before.
             *
             * Nothing here needs admin: copying makes no request, and whoever
             * opens the link adds THEMSELVES, so sharing a link is not the
             * same act as adding someone. The consequence, worth knowing: any
             * member can bring people into the group by passing the link on. */}
            <div className="h-px bg-border" />
            <button
              onClick={() => void handleInviteViaLink()}
              className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-muted/50 transition-colors"
            >
              <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center">
                <Link className="w-4 h-4 text-muted-foreground" />
              </div>
              <span className="text-sm text-foreground">{t("presence.inviteViaLink")}</span>
            </button>
            <div className="h-px bg-border" />

            {filteredMembers.map(member => {
              // A member row opens a chat with them. It looked clickable —
              // hover-highlighted — and did nothing, so Group Details was a
              // list of people you could not reach from it. Not your own row.
              const openChat = onMessageMember && member.id !== myUserId
                ? () => onMessageMember({ id: member.id, name: member.name, imageUrl: member.imageUrl })
                : undefined
              return (
              <div
                key={member.id}
                role={openChat ? "button" : undefined}
                tabIndex={openChat ? 0 : undefined}
                title={openChat ? t("panels.chatWith", { name: member.name }) : undefined}
                onClick={openChat}
                onKeyDown={openChat ? e => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); openChat() } } : undefined}
                className={`flex items-center gap-3 px-4 py-2.5 hover:bg-muted/50 transition-colors group ${openChat ? "cursor-pointer" : ""}`}
              >
                <div className="w-9 h-9 rounded-full bg-muted overflow-hidden shrink-0">
                  <AvatarPhoto
                    src={member.imageUrl}
                    alt={member.name}
                    className="w-full h-full object-cover"
                    fallback={<div className="w-full h-full flex items-center justify-center text-xs font-medium text-muted-foreground">{member.name[0]?.toUpperCase() || "?"}</div>}
                    contactId={member.id}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground truncate">{member.name}</p>
                  <MemberOnlineLabel memberId={member.id} fallback={!!member.isOnline} />
                </div>
                {member.isAdmin && <span className="text-[10px] text-primary border border-primary/30 px-1.5 py-0.5 rounded-full shrink-0">Admin</span>}
                {/* Admin actions, for admins, on other people, never on the
                    creator. On your own row they let the only admin demote
                    themselves and then be refused when undoing it (leaving is
                    Exit group); a non-admin could only get an error; and any
                    admin could strip or remove the creator. The server does not
                    enforce the creator rule yet, so this is the UI's half. */}
                {myIsAdmin && member.id !== myUserId && member.id !== wireConv?.createdBy && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="p-1 rounded-full hover:bg-muted opacity-0 group-hover:opacity-100 transition-opacity shrink-0" onClick={e => e.stopPropagation()}>
                      <MoreVertical className="w-3.5 h-3.5 text-muted-foreground" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44" onClick={e => e.stopPropagation()}>
                    <DropdownMenuItem onClick={() => void handleSetAdmin(member.id, !member.isAdmin)}>
                      <Shield className="w-4 h-4 mr-2" />{member.isAdmin ? t("presence.removeAdmin") : t("presence.makeAdmin")}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem variant="destructive" onClick={() => void handleRemoveMember(member.id)}>
                      <UserX className="w-4 h-4 mr-2" />{t("presence.removeMember")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                )}
              </div>
              )
            })}

            {filteredMembers.length === 0 && memberSearch && (
              <div className="px-4 py-4 text-center text-sm text-muted-foreground">
                {t("presence.noResults")}
              </div>
            )}
          </div>
        )}

        {/* Groups in common — personal only */}
        {!isGroup && groupsInCommon.length > 0 && (
          <div className="mx-4 mb-3 rounded-2xl border border-border bg-card overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
              <Users className="w-4 h-4 text-muted-foreground" />
              <p className="text-sm font-medium text-foreground">{groupsInCommon.length} group{groupsInCommon.length !== 1 ? "s" : ""} in common</p>
            </div>
            {groupsInCommon.map(group => {
              const gi = group.name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()
              const ci = group.name.charCodeAt(0) % colors.length
              return (
                <div key={group.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted/50 transition-colors">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold overflow-hidden shrink-0 ${colors[ci]}`}>
                    <AvatarPhoto src={group.imageUrl} alt={group.name} className="w-full h-full object-cover" fallback={gi} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground truncate">{group.name}</p>
                    <p className="text-xs text-muted-foreground">{group.participants?.length ?? 0} members</p>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Danger zone */}
        <div className="mx-4 mb-6 rounded-2xl border border-border bg-card overflow-hidden">
          {clearableCount > 0 && !hasLeftGroup && (
            <>
              <button onClick={() => setConfirmType("clear")} className="w-full flex items-center justify-center px-4 py-3 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-red-500">
                <div className="flex items-center gap-2">
                  <MessageSquareX className="w-4 h-4" />
                  <span className="text-sm">{t("presence.clearChat")}</span>
                </div>
              </button>
              <div className="h-px bg-border" />
            </>
          )}
          {/* Delete chat only hides the thread and keeps membership, so a
              group offers Exit group instead: hiding a group would leave the
              user in it, unseen, until someone posts. Every member may exit,
              admin or not. */}
          {isGroup && hasLeftGroup ? (
            // Left already: the group is kept read-only until this removes it
            // from MY chats. The group itself carries on for everyone else.
            <button onClick={() => setConfirmType("deleteGroup")} className="w-full flex items-center justify-center px-4 py-3 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-red-500">
              <div className="flex items-center gap-2">
                <Trash2 className="w-4 h-4" />
                <span className="text-sm">{t("presence.deleteGroupAction")}</span>
              </div>
            </button>
          ) : isGroup ? (
            <button onClick={() => setConfirmType("exit")} className="w-full flex items-center justify-center px-4 py-3 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-red-500">
              <div className="flex items-center gap-2">
                <LogOut className="w-4 h-4" />
                <span className="text-sm">{t("presence.exitGroupAction")}</span>
              </div>
            </button>
          ) : (
            <button onClick={() => setConfirmType("delete")} className="w-full flex items-center justify-center px-4 py-3 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-red-500">
              <div className="flex items-center gap-2">
                <LogOut className="w-4 h-4" />
                <span className="text-sm">{t("presence.deleteChat")}</span>
              </div>
            </button>
          )}
        </div>

        {isGroup && createdAtLabel && (
          <div className="px-6 pb-6 text-center">
            <p className="text-xs text-muted-foreground">
              {groupCreatedByMe
                ? t("presence.groupCreatedByYouOn", { date: createdAtLabel })
                : groupCreatorName
                  ? t("presence.groupCreatedByOn", { name: groupCreatorName, date: createdAtLabel })
                  : t("presence.groupCreatedOn", { date: createdAtLabel })}
            </p>
          </div>
        )}
      </div>

      {/* Confirm dialog */}
      <Dialog
        open={!!confirmType}
        onOpenChange={open => {
          if (!open) setConfirmType(null)
          else setKeepStarred(true)
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {confirmType === "clear" ? t("presence.clearChat")
                : confirmType === "exit" ? t("presence.exitGroup")
                : confirmType === "deleteGroup" ? t("presence.deleteGroup")
                : t("presence.deleteChat")}
            </DialogTitle>
            <DialogDescription>
              {confirmType === "clear" ? t("presence.clearChatDesc")
                : confirmType === "exit" ? t("presence.exitGroupDescWithName", { name: liveTitle })
                : confirmType === "deleteGroup" ? t("presence.deleteGroupDescWithName", { name: liveTitle })
                : t("presence.deleteChatDesc")}
            </DialogDescription>
          </DialogHeader>
          {/* Starred messages don't come into it when exiting: there is no
              thread content being cleared, only my membership. */}
          {confirmType !== "exit" && confirmType !== "deleteGroup" && (
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <Checkbox checked={keepStarred} onCheckedChange={(v) => setKeepStarred(v === true)} />
              {t("presence.keepStarredMessages")}
            </label>
          )}
          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => setConfirmType(null)}>{t("common.cancel")}</Button>
            <Button variant="destructive" onClick={() => {
              if (confirmType === "clear") { onClearChat?.({ keepStarred }); toast.success({ title: t("presence.chatCleared"), description: t("presence.chatClearedDesc") }) }
              else if (confirmType === "exit") { onExitGroup?.() }
              else if (confirmType === "deleteGroup") { onDeleteChat?.({ keepStarred: false }) }
              else { onDeleteChat?.({ keepStarred }) }
              setConfirmType(null)
            }}>
              {confirmType === "clear" ? t("common.clear")
                : confirmType === "exit" ? t("common.exit")
                : t("common.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AddMembersDialog
        open={addMembersOpen}
        onOpenChange={setAddMembersOpen}
        existingMemberIds={members.map(m => m.id)}
        onAdd={handleAddMembers}
      />

    </div>
  )
}
